import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appMessage as message } from '../utils/appMessage';
import { Card } from '../types/card';
import { saveDecidedPrice, updatePriceManually } from '../api/api';
import { currentRequestStore, requestIdForSave, saveErrorOutcome, saveSettled, settleSave } from '../utils/decidedPrice';
import { decidedCardPatch, decidedSaveNotice } from '../utils/decidedCardPatch';
import { DROP_STATUSES } from '../constants/constants';
import { confirmDecidedPrice } from '../components/syncConfirm/DecidedConfirm';
import { currentStoreHeader } from '../http';
import { useNavigation } from '../contexts/NavigationContext';
import { useDailyWorkflowStorage } from './UseDailyWorkflowStorage';

// ─── useInlinePriceEdit ───────────────────────────────────────────────────────
// UseCardData.ts(393줄, useCards/useInlinePriceEdit/useCardJobs 세 관심사가 한 파일)를
// 파일별로 나눈 것 중 하나(2026-08-11, 로직 변경 없는 기계적 이동) — useCardData()가
// 이 세 훅을 조합한다.

export function useInlinePriceEdit(
  replaceCard: (card: Card) => void,
  storage: ReturnType<typeof useDailyWorkflowStorage>,
) {
  const [editingId, setEditingId]       = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState<number | null>(null);
  const [savingId, setSavingId]         = useState<number | null>(null);
  const [bulkSaving, setBulkSaving]     = useState(false);
  // ② 적용가 확인 창(감사 #692) — 떠 있는 창 하나. 화면이 내려가면(로그아웃 · 세션 만료 · 매장 전환 재마운트) 닫는다(감사 #827).
  const confirmRef = useRef<AbortController | null>(null);
  useEffect(() => () => { confirmRef.current?.abort(); confirmRef.current = null; }, []);
  // 메뉴를 옮겨도 이 훅(카드 컨텍스트)은 살아 있다 — 화면이 바뀌면 떠 있는 확인을 닫는다(③ 확인 창과 같은 처리, 감사 #860).
  const { page } = useNavigation();
  useEffect(() => { confirmRef.current?.abort(); confirmRef.current = null; }, [page]);

  // ── pendingConfirmIds: 초기값을 localStorage 에서 복원 ─────────────────────
  const [pendingConfirmIds, setPendingConfirmIdsRaw] = useState<Set<number>>(
    () => new Set(storage.readPendingIds()),
  );

  // Set 을 직접 직렬화할 수 없으므로 변경될 때마다 storage 에 동기적으로 저장.
  // useRef 로 최신 setter 를 잡아 두어 콜백 내부에서 클로저 stale 이 생기지 않게 한다.
  const setPendingConfirmIds = useCallback(
    (updater: Set<number> | ((prev: Set<number>) => Set<number>)) => {
      setPendingConfirmIdsRaw((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        storage.savePendingIds(next);   // localStorage 동기 저장
        return next;
      });
    },
    [storage],
  );

  const startEdit = useCallback((card: Card) => {
    setEditingId(card.id);
    setEditingPrice(card.suggestedPrice ?? card.currentPrice);
  }, []);

  // 수정 가격 조회를 여러 번 돌리면(재조회·재시도 등) 예전엔 매번 새 하락 id를 기존 대기
  // 목록에 합집합만 해서, 이전 조회 때 하락이었다가 이번엔 아닌 카드의 id가 대기 목록에
  // 영원히 남아 쌓이는 버그가 있었다(예: 72건인데 727건으로 표시, 2026-07-28 사용자 발견).
  //
  // 처음엔 "이번 결과에 없는 id는 건드리지 않고, 있는 id만 빼거나 더하는" diff 방식으로
  // 고쳤는데, 실사용에서 diff 후에도 소수의 stale id가 계속 남는 사례가 재현됐다(부산
  // 401 vs 72, 광주 668 vs 667 — 원인은 못 찾음, 아마 여러 탭/이전 세션에서 남은
  // localStorage 값과의 경합으로 추정). "전체 N건 조회"는 항상 카드 전체를 대상으로
  // 돌기 때문에(DailyWorkflowBar, 부분 대상 호출 경로 없음) diff가 아니라 이번 결과로
  // 통째로 교체하는 쪽이 원인이 뭐든 안전하다 — 대기 목록은 "지금 이 순간의 하락 카드
  // 집합"과 정확히 같아야 하므로.
  //
  // 예외: 일부 카드만 다시 받는 경로(반영 로그의 '실패 N건 가격 다시 받기', 유저 플로우 제안 1 · 2026-11-03)는 결과가 전체가 아니라
  // partial 로 받은 카드만 고친다(하락이면 넣고 아니면 뺀다) — 통째로 바꾸면 다른 카드의 대기가 사라져 ③ 이 검토 없이 열린다.
  const reconcilePendingDrops = useCallback((results: Card[], opts?: { partial?: boolean }) => {
    if (opts?.partial) {
      setPendingConfirmIds((prev) => {
        const next = new Set(prev);
        results.forEach((c) => {
          if (DROP_STATUSES.includes(c.priceChangeStatus)) next.add(c.id); else next.delete(c.id);
        });
        return next;
      });
      return;
    }
    const stillDropIds = new Set(
      results.filter((c) => DROP_STATUSES.includes(c.priceChangeStatus)).map((c) => c.id),
    );
    setPendingConfirmIds(stillDropIds);
  }, [setPendingConfirmIds]);

  const removePending = useCallback((id: number) => {
    setPendingConfirmIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, [setPendingConfirmIds]);

  const removePendingMany = useCallback((ids: number[]) => {
    setPendingConfirmIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, [setPendingConfirmIds]);

  // removePendingMany의 역연산 — "시세 범위 내 N건 일괄 적용"을 되돌릴 때 쓴다(대기
  // 목록에 다시 넣어 반영 대상에서 빼고 ③ 스토어 반영을 다시 잠근다).
  const restorePendingMany = useCallback((ids: number[]) => {
    setPendingConfirmIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, [setPendingConfirmIds]);

  const saveManual = useCallback(async (id: number, price: number): Promise<boolean> => {
    setSavingId(id);
    try {
      replaceCard(await updatePriceManually(id, price));
      return true;
    } catch {
      message.error('가격 수정 중 오류가 발생했습니다.');
      return false;
    } finally {
      setSavingId(null);
    }
  }, [replaceCard]);

  /**
   * ② 적용가(입력한 금액으로 적용)는 판매가 결정 API 로(감사 card-controltower#538 · #558 M4, 백엔드 PR #77 Q2) — 저장 · 네이버 반영 ·
   * PriceHub 보고가 한 번에 간다. 예전 PATCH /price 는 PriceHub 모르게 수정가만 바꿔, PriceHub 값이 그대로면 다음 가격 받기가
   * 덮거나(보호 전) PriceHub 와 어긋난 채 남았다. '유지(✗)' 는 PATCH 그대로(saveManual) — 사람이 정한 가격 보호(priceHeld)로 남는다.
   * 저장이 됐으면(네이버 실패 · 결과 불명이어도 서버는 결정을 남긴다) true, 서버가 거절 · 응답 없음이면 false(대기 목록에 남긴다).
   */
  const saveDecided = useCallback(async (card: Card, price: number): Promise<boolean> => {
    if (!(price > 0)) { message.warning('적용할 가격은 1원 이상이어야 합니다.'); return false; }
    // 바로 네이버에 쓰니 ③ 처럼 미리보기 확인 창 뒤에 보낸다(감사 card-controltower#692 · 결정 #652). 취소면 아무것도 보내지 않고
    // 대기 목록에 남긴다(false). 저장 중 표시는 확인 뒤부터 — 창이 떠 있는 동안은 부른 쪽(검토 창 busy)이 잠근다.
    const store = currentRequestStore();
    // 미리보기 · 저장을 창을 연 시점 매장으로 고정한다(storeOverride, 감사 #845 — ③ 과 같은 방식).
    const header = currentStoreHeader();
    // 연타 방지는 이 훅의 ref 로(감사 #827) — 창이 하나 떠 있으면 새로 띄우지 않는다. 화면이 내려가면 abort 로 창을 닫고 풀린다.
    if (confirmRef.current) return false;
    const ctrl = new AbortController();
    confirmRef.current = ctrl;
    let confirmed = false;
    try {
      confirmed = await confirmDecidedPrice(card, price, { signal: ctrl.signal, store, header });
    } finally {
      if (confirmRef.current === ctrl) confirmRef.current = null;
    }
    if (!confirmed || ctrl.signal.aborted) return false;
    // 창이 떠 있는 동안 매장이 바뀌었으면(SUPER 전환 · 다른 탭 로그인) 이 카드 id 를 다른 매장으로 보내게 된다 — 보내지 않는다.
    if (currentRequestStore() !== store) {
      message.warning({ key: `decided-${card.id}`, content: '매장이 바뀌어 적용가를 보내지 않았습니다 — 다시 확인해 주세요', duration: 8 });
      return false;
    }
    setSavingId(card.id);
    // 결과 불명 뒤 같은 가격으로 다시 저장하면 같은 요청 번호(#651).
    const requestId = requestIdForSave(store, card.id, price);
    try {
      const r = await saveDecidedPrice(card.id, { price, requestId }, { store: header });
      if (saveSettled(r.naver.status)) settleSave(store, card.id, requestId);
      replaceCard(decidedCardPatch(card, r.price, r.naver.status === 'SUCCESS'));
      const n = decidedSaveNotice(r, card.productName ? `${card.productName}` : '적용가');
      message[n.level]({ key: `decided-${card.id}`, content: n.text, duration: n.level === 'success' ? 3 : 8 });
      return true;
    } catch (e) {
      const o = saveErrorOutcome(e);
      if (saveSettled(null, o.kind)) settleSave(store, card.id, requestId);
      message.error({
        key: `decided-${card.id}`,
        content: o.kind === 'unknown' ? `${o.message} — 반영 로그에서 확인한 뒤 다시 적용하세요` : `적용가를 저장하지 못했습니다 — ${o.message}`,
        duration: 8,
      });
      return false;
    } finally {
      setSavingId(null);
    }
  }, [replaceCard]);

  const handleConfirm = useCallback(async (card: Card) => {
    const isDirty =
      editingId === card.id &&
      editingPrice !== null &&
      editingPrice !== card.suggestedPrice;

    if (isDirty) {
      if (editingPrice! < 0) { message.warning('올바른 가격을 입력해 주세요.'); return; }
      if (!await saveDecided(card, editingPrice!)) return;
    }
    removePending(card.id);
    setEditingId(null);
  }, [editingId, editingPrice, saveDecided, removePending]);

  const handleReject = useCallback(async (card: Card) => {
    if (card.currentPrice !== null) {
      if (!await saveManual(card.id, card.currentPrice)) return;
    }
    removePending(card.id);
    setEditingId(null);
  }, [saveManual, removePending]);

  // ── 포커스 리뷰 전용 액션 ──────────────────────────────────────────────────

  const applyDrop = useCallback(async (card: Card, override?: number): Promise<boolean> => {
    if (override != null && override !== card.suggestedPrice) {
      if (override < 0) { message.warning('올바른 가격을 입력해 주세요.'); return false; }
      if (!await saveDecided(card, override)) return false;
    }
    removePending(card.id);
    return true;
  }, [saveDecided, removePending]);

  const keepDrop = useCallback(async (card: Card): Promise<boolean> => {
    if (card.currentPrice !== null) {
      if (!await saveManual(card.id, card.currentPrice)) return false;
    }
    removePending(card.id);
    return true;
  }, [saveManual, removePending]);

  const applyDropsBulk = useCallback((cards: Card[]) => {
    removePendingMany(cards.map((c) => c.id));
  }, [removePendingMany]);

  const keepDropsBulk = useCallback(async (cards: Card[]): Promise<boolean> => {
    const toSave = cards.filter((c) => c.currentPrice !== null);
    setBulkSaving(true);
    try {
      const updated = await Promise.all(
        toSave.map((c) => updatePriceManually(c.id, c.currentPrice as number)),
      );
      updated.forEach(replaceCard);
      removePendingMany(cards.map((c) => c.id));
      return true;
    } catch {
      message.error('일괄 유지 처리 중 오류가 발생했습니다.');
      return false;
    } finally {
      setBulkSaving(false);
    }
  }, [replaceCard, removePendingMany]);

  return useMemo(() => ({
    editingId, editingPrice, setEditingPrice, savingId, bulkSaving, pendingConfirmIds,
    startEdit, reconcilePendingDrops, removePending, removePendingMany, restorePendingMany,
    handleConfirm, handleReject,
    applyDrop, keepDrop, applyDropsBulk, keepDropsBulk,
  }), [
    editingId, editingPrice, setEditingPrice, savingId, bulkSaving, pendingConfirmIds,
    startEdit, reconcilePendingDrops, removePending, removePendingMany, restorePendingMany,
    handleConfirm, handleReject,
    applyDrop, keepDrop, applyDropsBulk, keepDropsBulk,
  ]);
}
